import { HStack, VStack, Image, Button, Text, Box } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useCallback, useState, useRef } from "react";
import { useLoading } from "src/Manager/hooks/form";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";
import KYDInput from "./KYDInput";
import KYDLoading from "./KYDLoading";

const KYDImageUpload = ({
  image,
  link,
  supportLink = false,
  onChange,
  isDisabled = false,
  createUploadUrl,
  uploadTitle = "",
  buttonLabel = "Add Image",
  showDragDropHint = false,
  aspectRatio = "1",
}: {
  image: string | null;
  link?: string;
  supportLink?: boolean;
  onChange: (result: {
    cdn_url: string;
    temp_url: string;
    link?: string;
  }) => void;
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: ({ signedUrl }) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  isDisabled?: boolean;
  uploadTitle?: string;
  buttonLabel?: string;
  showDragDropHint?: boolean;
  aspectRatio?: string;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useLoading({
    upload: false,
  });
  const [progress, setProgress] = useState(0);
  const dragCounter = useRef(0);
  const uploadRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      // FIND A WAY TO UPLOAD THIS FILE
      console.log("Dropped a file", file);
    },
    [onChange]
  );

  return (
    <VStack
      w="100%"
      borderRadius="8px"
      p={3}
      gap={3}
      alignItems="center"
      justifyContent="center"
      transition="border 0.2s ease"
    >
      <HStack
        borderRadius="8px"
        gap={3}
        w="100%"
        alignItems="center"
        justifyContent="center"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        border={isDragging ? "2px dashed #3182CE" : "0px dashed transparent"}
        transition="border 0.2s ease"
      >
        <ReactS3Uploader
          style={{ display: "none" }}
          //@ts-ignore
          inputRef={uploadRef}
          getSignedUrl={(file, callback) => createUploadUrl({ file, callback })}
          accept="image/*"
          onProgress={(p) => {
            setProgress(p);
          }}
          preprocess={(f, next) => {
            setLoading("upload", true);
            next(f);
          }}
          onError={(e) => {
            console.log("onError", e);
            console.error(e);
          }}
          onFinish={(result: S3Response & { cdn_url: string }, file) => {
            setLoading("upload", false);
            onChange({
              cdn_url: result.cdn_url,
              temp_url: URL.createObjectURL(file),
              link: link,
            });
          }}
          uploadRequestHeaders={(file) => ({
            "Content-Type": file.type,
            "x-amz-acl": "private",
          })}
          contentDisposition="auto"
        />
        <VStack>
          {!loading.upload && (
            <VStack w="100%">
              {uploadTitle && (
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  {uploadTitle}
                </Text>
              )}
              {!image && (
                <>
                  {showDragDropHint && (
                    <Text fontSize="12px" color="#1A202CB2" paddingBottom={4}>
                      Drag & drop file here
                    </Text>
                  )}
                  <Button
                    type="button"
                    onClick={() => uploadRef.current?.click()}
                    isDisabled={isDisabled}
                    border="1px solid #E2E2E2"
                    variant="outline"
                    fontWeight="400"
                    colorScheme="gray"
                    fontSize="13px"
                    gap={2}
                    borderRadius="lg"
                    bg="white"
                    dropShadow="0px 2px 6px rgba(0,0,0,0.05)"
                  >
                    {buttonLabel}
                    <AddIcon color="#5D5D5D" />
                  </Button>
                </>
              )}
            </VStack>
          )}
          {loading.upload ? (
            <Box
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              width="150px"
              height="150px"
              borderRadius="2px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <KYDLoading strokeColor="#1A202C" />
            </Box>
          ) : (
            image && (
              <VStack>
                <Box
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  width="150px"
                  borderRadius="2px"
                >
                  <Image
                    boxSize="100px"
                    height="150px"
                    width="auto"
                    src={image}
                    bg="transparent"
                    boxShadow="0px 4px 10px rgba(0,0,0,0.1), 0px 2px 4px rgba(0,0,0,0.05)"
                    objectFit="cover"
                    alt="preview"
                    borderRadius="2px"
                    mx="auto"
                    aspectRatio={aspectRatio}
                  />
                </Box>
                <HStack>
                  <Text
                    as="button"
                    color="blue.500"
                    mr={4}
                    fontSize="sm"
                    fontWeight="semibold"
                    textDecoration="underline"
                    background="none"
                    border="none"
                    cursor="pointer"
                    _hover={{ color: "blue.700" }}
                    onClick={() => uploadRef.current?.click()}
                  >
                    Replace
                  </Text>
                  <Text
                    as="button"
                    color="blue.500"
                    fontSize="sm"
                    fontWeight="semibold"
                    textDecoration="underline"
                    background="none"
                    border="none"
                    cursor="pointer"
                    _hover={{ color: "blue.700" }}
                    onClick={() =>
                      onChange({
                        cdn_url: "",
                        temp_url: "",
                        link: "",
                      })
                    }
                  >
                    Remove
                  </Text>
                </HStack>
              </VStack>
            )
          )}
        </VStack>
      </HStack>
      {supportLink && (
        <KYDInput
          label="Link"
          placeholder="Add link to redirect when image is clicked"
          value={link}
          onChange={(e) => {
            onChange({
              cdn_url: image,
              temp_url: image,
              link: e.target.value,
            });
          }}
        />
      )}
    </VStack>
  );
};

export default KYDImageUpload;
